import React, {Fragment, useMemo, useState} from 'react';

import FileInput from 'react-fine-uploader/file-input';
import {useSelector} from 'react-redux';
import styled from 'styled-components';

import {authTypeSelector} from 'app/auth/slice';
import {LazyTooltip} from 'app/components/general/LazyTooltip';
import {CurrentUserSelector} from 'app/current-user/api';
import FineUploaderFileList from 'app/general/widgets/FineUploaderFileList';
import {
  FileAccessLink,
  GuestFileAccessLink,
} from 'app/mediaaccess/components/FileAccess';
import {useAccountSettings} from 'app/settings/api';
import {preparedUploader, removeMediaPath} from 'app/utils/fineuploader';
import {gt} from 'app/utils/i18n';

import {FieldLastEditStamp} from '../FieldLastEditStamp';

import type {StepTaskFieldProps} from '../commonprops';

const PENDING_STATUSES = [
  'deleting',
  'retrying_upload',
  'submitting',
  'uploading',
];

export function StepTaskFileField({
  field,
  isSaving,
  progress,
  value,
  blocked,
  inputValueChanged,
}: StepTaskFieldProps): JSX.Element {
  const settings = useAccountSettings(undefined, {
    refetchOnMountOrArgChange: 900,
  }).data!;
  const {
    accountId,
    mediaURL,
    MEDIA_URL_PROTECTED,
    s3Upload,
    authToken,
    isGuest,
  } = useSelector((state) => {
    const auth = authTypeSelector(state);
    const user = CurrentUserSelector(state).data!;
    return {
      isGuest: user.is_guest_until != null,
      accountId: settings.account_id,
      mediaURL: settings.MEDIA_URL,
      MEDIA_URL_PROTECTED: settings.MEDIA_URL_PROTECTED,
      s3Upload: settings.s3Upload['TASKFILES'],
      authToken: auth.type === 'token' ? auth.token : null,
    };
  });

  const [state, setState] = useState<{
    status: undefined | string;
    submittedFiles: Array<{
      id: number;
      name: string;
    }>;
  }>({submittedFiles: [], status: undefined});

  const uploader = useMemo(
    () =>
      preparedUploader(
        {
          ...s3Upload,
          uploadCustomHeaders:
            authToken != null
              ? {
                  Authorization: `Bearer ${authToken}`,
                }
              : undefined,
        },
        accountId,
        mediaURL
      ),
    []
  );

  const handleStatusChange = (
    status: string,
    oldStatus: string | null,
    id: number
  ) => {
    switch (status) {
      case 'submitted':
        return handleSubmitted(id);

      case 'upload_successful':
        return handleUploadSuccess(id);

      case 'canceled':
        return setState({
          status: undefined,
          submittedFiles: state.submittedFiles.filter((file) => file.id !== id),
        });

      default:
        setState((state) => ({
          ...state,
          status,
        }));
    }
  };

  const handleSubmitted = (id: number): void => {
    setState({
      status: 'submitted',
      submittedFiles: state.submittedFiles.concat([
        {
          id,
          name: uploader.methods.getName(id),
        },
      ]),
    });
  };

  const handleUploadSuccess = (id: number): void => {
    const fileRef = removeMediaPath(uploader.methods.getKey(id), mediaURL);
    // Encode file name before appending as query string
    const url = new URL(fileRef, window.location.origin);
    url.searchParams.set('name', uploader.methods.getName(id));
    setState((state) => ({...state, status: undefined}));
    inputValueChanged(fileRef + url.search);
  };

  const cancel = (id: number): void => {
    uploader.methods.cancel(id);
  };

  const getFilename = (path: string): string => {
    const decodedFilename = decodeURIComponent(path);
    const url = new URL(decodedFilename, window.location.origin);

    return (
      url.searchParams.get('name') ||
      url.pathname.split('/').pop() ||
      'Unnamed File'
    );
  };

  const clearValue = () => {
    setState({
      status: undefined,
      submittedFiles: [],
    });
    inputValueChanged('');
  };

  const retry = (id: number) => {
    uploader.methods.retry(id);
  };

  const {status, submittedFiles} = state;
  const uploading =
    submittedFiles.length > 0 && status != null ? submittedFiles[0] : undefined;

  const FileAccessButton = isGuest ? GuestFileAccessLink : FileAccessLink;

  return (
    <div className="form-group row mb-0">
      <div className="col-12">
        <label>{field.label}</label>
        <div className="input-group">
          {value !== '' ? (
            <Fragment>
              <div className="form-control text-truncate">
                <i className="mr-2 far fa-paperclip" />
                <FileAccessButton
                  MEDIA_URL_PROTECTED={MEDIA_URL_PROTECTED}
                  file_ref={value}
                  file_name={getFilename(value)}
                />
              </div>
              <LazyTooltip tooltip={gt('Remove file')}>
                <div className="input-group-append">
                  <button
                    disabled={isSaving || blocked}
                    className="btn btn-light"
                    onClick={clearValue}
                  >
                    <i className="far fa-times" />
                  </button>
                </div>
              </LazyTooltip>
              <div className="input-group-append">
                <button disabled={true} className="btn btn-secondary">
                  <i
                    className={`fa fa-fw ${
                      isSaving ? 'fa-circle-notch fa-spin' : 'fa-check'
                    }`}
                  />
                </button>
              </div>
            </Fragment>
          ) : uploading != null ? (
            <Fragment>
              <div className="form-control text-truncate">{uploading.name}</div>
              {status != null && PENDING_STATUSES.indexOf(status) > -1 ? (
                <Fragment>
                  <div className="input-group-append">
                    <button
                      className="btn btn-light"
                      onClick={() => cancel(uploading.id)}
                    >
                      {gt('Cancel')}
                    </button>
                  </div>
                  <div className="input-group-append">
                    <button disabled={true} className="btn btn-secondary">
                      <i className="fa fa-fw fa-circle-notch fa-spin" />
                    </button>
                  </div>
                </Fragment>
              ) : status === 'upload_failed' ? (
                <div className="input-group-append">
                  <button
                    className="btn btn-light"
                    onClick={() => retry(uploading.id)}
                  >
                    <i className="fas fa-fw fas fa-sync-alt" /> {gt('Retry')}
                  </button>
                </div>
              ) : null}
            </Fragment>
          ) : (
            <StyledCustomFileDiv>
              <FileInput id={`field:${field.id}`} uploader={uploader} />
              <label
                className="custom-file-label"
                htmlFor={`field:${field.id}`}
              >
                {gt('Choose a file...')}
              </label>
            </StyledCustomFileDiv>
          )}
        </div>
        <FineUploaderFileList
          onlyProgress={true}
          uploader={uploader}
          files={state.submittedFiles.map(({id}) => id)}
          onCancelFile={cancel}
          onStatusChange={handleStatusChange}
        />
        <FieldLastEditStamp progress={progress} />
      </div>
    </div>
  );
}

const StyledCustomFileDiv = styled.div.attrs({
  className: 'custom-file',
})`
  .custom-file-label {
    cursor: pointer;
    z-index: auto;
  }
`;
